import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Drawer,
  EmptyState,
  Field,
  IconBtn,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  useConfirm,
  useToast,
} from "../../../design-system";
import { Icon } from "../../../design-system/Icon";
import useCurrentMembership from "../../../helpers/useCurrentMembership";
import { formatInstant } from "../../../helpers/datetime";
import {
  createInspectionToken,
  listInspectionTokens,
  revokeInspectionToken,
} from "../../../helpers/kronaly_helper";

function unwrap(r) {
  if (r && typeof r === "object" && "data" in r) return r.data;
  return r;
}

// Instante en la zona del workspace.
function formatDateTime(iso, tz) {
  return formatInstant(iso, tz);
}

function statusOf(row) {
  if (row.revoked_at) return { tone: "danger", label: "Revocado" };
  if (!row.is_active) return { tone: "neutral", label: "Caducado" };
  return { tone: "success", label: "Activo" };
}

export default function InspeccionTokensPage() {
  document.title = "Tokens de Inspección | Kronaly";
  const { role, loading: membershipLoading, timezone } = useCurrentMembership();
  const toast = useToast();
  const confirm = useConfirm();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const isOwner = role === "owner";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listInspectionTokens();
      setTokens(unwrap(r) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los tokens.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!membershipLoading && isOwner) load();
  }, [membershipLoading, isOwner, load]);

  const handleRevoke = async (row) => {
    const ok = await confirm({
      title: `Revocar "${row.label}"`,
      description:
        "El inspector que use este token recibirá 401 inmediatamente. La acción es irreversible.",
      confirmLabel: "Revocar token",
      tone: "danger",
    });
    if (!ok) return;
    setSavingId(row.id);
    try {
      await revokeInspectionToken(row.id);
      toast.success("Token revocado", `"${row.label}" ya no es válido.`);
      await load();
    } catch (e) {
      toast.error(
        "No se pudo revocar",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSavingId(null);
    }
  };

  if (membershipLoading) {
    return (
      <div className="kr-page" style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner />
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="kr-page">
        <EmptyState
          icon="ShieldOff"
          title="Acceso restringido"
          description="Solo el propietario del workspace puede gestionar los tokens de Inspección de Trabajo."
        />
      </div>
    );
  }

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Workspace · Cumplimiento"
        title="Tokens de Inspección de Trabajo"
        description="Acceso remoto regulatorio para Inspección de Trabajo. Cada token permite consulta de solo lectura del histórico de fichaje del workspace."
        actions={
          <Button variant="primary" icon="Plus" onClick={() => setCreateOpen(true)}>
            Emitir nuevo token
          </Button>
        }
      />

      <Alert tone="info">
        <strong>Cómo funciona:</strong> emite un token con una etiqueta identificativa
        (organismo, expediente). El token solo se muestra <strong>una vez</strong> al crearlo —
        guárdalo en sitio seguro y entrégalo al inspector. Cada acceso queda registrado en el log
        de inspección con IP, endpoint consultado y momento.
      </Alert>

      {error && <Alert tone="danger">{error}</Alert>}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <Spinner />
        </div>
      ) : tokens.length === 0 ? (
        <EmptyState
          icon="Shield"
          title="Aún no hay tokens emitidos"
          description="Emite uno cuando recibas una solicitud de Inspección o quieras tener uno listo."
          action={{
            label: "Emitir token",
            icon: "Plus",
            onClick: () => setCreateOpen(true),
          }}
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <th style={th}>Etiqueta</th>
              <th style={th}>Emitido</th>
              <th style={th}>Caduca</th>
              <th style={th}>Último uso</th>
              <th style={th}>Estado</th>
              <th style={{ ...th, textAlign: "right" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {tokens.map((row) => {
              const status = statusOf(row);
              const canRevoke = !row.revoked_at;
              return (
                <tr key={row.id}>
                  <td style={td}>
                    <strong>{row.label}</strong>
                  </td>
                  <td style={td}>{formatDateTime(row.created_at, timezone)}</td>
                  <td style={td}>
                    {row.expires_at ? formatDateTime(row.expires_at, timezone) : (
                      <span style={{ color: "var(--kr-fg-muted)" }}>Sin caducidad</span>
                    )}
                  </td>
                  <td style={td}>
                    {row.last_used_at ? formatDateTime(row.last_used_at, timezone) : (
                      <span style={{ color: "var(--kr-fg-muted)" }}>Nunca usado</span>
                    )}
                  </td>
                  <td style={td}>
                    <Badge tone={status.tone}>{status.label}</Badge>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {canRevoke && (
                      <IconBtn
                        icon="Ban"
                        title="Revocar"
                        onClick={() => handleRevoke(row)}
                        disabled={savingId === row.id}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <CreateTokenDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
        toast={toast}
        tz={timezone}
      />
    </div>
  );
}

function CreateTokenDrawer({ open, onClose, onCreated, toast, tz }) {
  const [label, setLabel] = useState("");
  const [daysValid, setDaysValid] = useState("90");
  const [submitting, setSubmitting] = useState(false);
  // Cuando el create devuelve el plain text, mostramos vista de confirmación
  // en el mismo drawer (no podemos volver a verlo después).
  const [createdToken, setCreatedToken] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setLabel("");
      setDaysValid("90");
      setSubmitting(false);
      setCreatedToken(null);
      setCopied(false);
    }
  }, [open]);

  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const payload = { label: trimmed };
      if (daysValid && daysValid !== "0") {
        payload.days_valid = parseInt(daysValid, 10);
      }
      const r = await createInspectionToken(payload);
      const data = r && typeof r === "object" && "data" in r ? r.data : r;
      setCreatedToken(data);
      await onCreated();
    } catch (e) {
      toast.error(
        "No se pudo emitir el token",
        typeof e === "string" ? e : e?.message || "Error desconocido",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!createdToken?.plain_text) return;
    try {
      await navigator.clipboard.writeText(createdToken.plain_text);
      setCopied(true);
      toast.success("Copiado", "Token copiado al portapapeles.");
    } catch (_) {
      // Fallback: el usuario lo copia manualmente
    }
  };

  if (createdToken) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        title="Token emitido"
        size="md"
        footer={
          <Button variant="primary" icon="Check" onClick={onClose}>
            He guardado el token
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Alert tone="warning">
            <strong>Este token solo se muestra una vez.</strong> Cópialo ahora y entrégalo al
            inspector por un canal seguro. No se puede recuperar después.
          </Alert>

          <Field label="Etiqueta">
            <Input value={createdToken.label} readOnly />
          </Field>

          <Field label="Token">
            <div style={tokenBox}>
              <code style={tokenCode}>{createdToken.plain_text}</code>
              <Button
                variant={copied ? "ghost" : "primary"}
                icon={copied ? "Check" : "Copy"}
                onClick={handleCopy}
                size="sm"
              >
                {copied ? "Copiado" : "Copiar"}
              </Button>
            </div>
          </Field>

          {createdToken.expires_at && (
            <Field label="Caduca">
              <Input value={formatDateTime(createdToken.expires_at, tz)} readOnly />
            </Field>
          )}

          <Alert tone="info">
            <strong>Uso por el inspector:</strong>
            <pre style={curlBox}>
{`curl -H "X-Inspection-Token: ${createdToken.plain_text}" \\
  https://<tu-dominio>/api/v1/inspection/workspace`}
            </pre>
          </Alert>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Emitir token de Inspección"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!label.trim() || submitting}
            icon="Shield"
          >
            {submitting ? "Emitiendo…" : "Emitir token"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Alert tone="info">
          Identifica el token con la entidad solicitante y, si lo hay, el número de expediente.
          Es lo que verás en el listado y en el log de accesos.
        </Alert>

        <Field label="Etiqueta" required>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ej. Inspección Trabajo Madrid — expediente 2026/00123"
            autoFocus
            maxLength={200}
          />
        </Field>

        <Field
          label="Validez"
          description="Tras este plazo el token deja de funcionar. Puedes revocarlo antes en cualquier momento."
        >
          <Select value={daysValid} onChange={(e) => setDaysValid(e.target.value)}>
            <option value="30">30 días</option>
            <option value="60">60 días</option>
            <option value="90">90 días (recomendado)</option>
            <option value="180">180 días</option>
            <option value="365">1 año</option>
            <option value="0">Sin caducidad</option>
          </Select>
        </Field>
      </div>
    </Drawer>
  );
}

const th = {
  textAlign: "left",
  padding: "10px 16px",
  borderBottom: "1px solid var(--kr-border)",
  background: "var(--kr-bg-subtle)",
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
};

const td = {
  padding: "12px 16px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "middle",
};

const tokenBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: 12,
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border)",
  borderRadius: 6,
};

const tokenCode = {
  flex: 1,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13,
  wordBreak: "break-all",
  color: "var(--kr-fg)",
};

const curlBox = {
  background: "var(--kr-bg-subtle)",
  border: "1px solid var(--kr-border)",
  borderRadius: 6,
  padding: 12,
  marginTop: 8,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12,
  whiteSpace: "pre-wrap",
  overflow: "auto",
};
